import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TableRow,
  TableCell,
  Stack,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import Layout from "../../layout";
import CustomDataTable from "../../components/table/CustomTable";
import CustomSearch from "../../components/search/Search";
import useDeviceTickets, { Ticket } from "../../hooks/useDeviceTickets";
import { formatDate } from "../../utils/helper";

const TicketsPage = () => {
  const [selectedMeterId, setSelectedMeterId] = useState("");
  const [searchMeterId, setSearchMeterId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: ticketsData, isLoading } = useDeviceTickets(searchMeterId);

  const handleSearch = () => {
    setSearchMeterId(selectedMeterId.trim());
    setPage(0);
  };

  const COLUMNS = [
    "Ticket Number",
    "Ticket ID",
    "Title",
    "Category",
    "Priority",
    "Status",
    "Created By",
    "Created At",
  ];

  const rowsContent = useMemo(() => {
    if (!ticketsData || !Array.isArray(ticketsData)) return [];

    return ticketsData
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((ticket: Ticket, index: number) => (
        <TableRow key={index} sx={{ height: "50px" }}>
          <TableCell>
            <Typography variant="tableBody" fontWeight={600} color="#1064ED">
              {ticket.TicketNumber}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.TicketId}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.Title}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.Category}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.Priority}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.Status}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">{ticket.CreatedBy}</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="tableBody">
              {formatDate(ticket.CreatedAt, "DD/MM/YYYY HH:mm:ss")}
            </Typography>
          </TableCell>
        </TableRow>
      ));
  }, [ticketsData, page, rowsPerPage]);

  return (
    <Layout>
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Grid container spacing={2} width={"100%"}>
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <CustomSearch
                  sx={{ width: "100%" }}
                  value={selectedMeterId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSelectedMeterId(e.target.value);
                  }}
                  onClick={handleSearch}
                  placeholder={"All Meter IDs / Device IDs"}
                />
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader title={<Typography variant="body1">Tickets</Typography>} />
        <Divider />
        <CardContent>
          {!searchMeterId ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography color="textSecondary">
                Please search for a Meter ID to view tickets.
              </Typography>
            </Box>
          ) : (
            <CustomDataTable
              sx={{ maxHeight: "600px" }}
              columns={COLUMNS}
              rowsContent={rowsContent}
              isLoading={isLoading}
              totalCount={ticketsData?.length || 0}
              pagination={true}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              Actions={true}
            />
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default TicketsPage;
